<script setup lang="ts">
import type { GameItem } from '~/stores/games'
import { DEFAULT_SECTION_TITLES, type TextSettings, type ThemeSettings, type SectionSettings } from '~/stores/auth'

const props = defineProps<{
  games: GameItem[]
  textSettings: TextSettings
  themeSettings: ThemeSettings
  sectionSettings: SectionSettings
  editMode?: boolean
}>()

const emit = defineEmits<{
  'open-games-manager': []
}>()

const toast = useToast()

const activeIndex = ref(0)

// Carousel items with basis class for multi-item view
const carouselItems = computed(() =>
  props.games.map(game => ({ ...game, class: 'basis-1/4' }))
)

function onClickPrev() {
  activeIndex.value--
}

function onClickNext() {
  activeIndex.value++
}

function onSelect(index: number) {
  activeIndex.value = index
}

function asGame(item: unknown): GameItem {
  return item as GameItem
}

// Heading and divider styles
const injectedResolvedFont = inject<ComputedRef<string>>('resolvedFontFamily', computed(() => ''))
const headingStyle = computed(() => ({
  fontSize: `calc(1rem * ${props.textSettings.fontScale})`,
  fontWeight: props.textSettings.fontWeight,
  fontFamily: `'${injectedResolvedFont.value || props.textSettings.fontFamily}', sans-serif`,
  color: `color-mix(in srgb, ${props.textSettings.color} 70%, transparent)`,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.1em'
}))

// Use parent-provided separator style, fallback to default gradient
const injectedSeparatorStyle = inject<Ref<Record<string, string> | null>>('separatorStyle', ref({ background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' }))
const dividerStyle = computed(() => injectedSeparatorStyle.value ?? { background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' })

// Section title visibility
const inCard = inject<Ref<boolean>>('sectionInCard', ref(false))
const showTitles = computed(() => props.sectionSettings.showTitles !== false)

// Custom section titles
const authStore = useAuthStore()
const { requirePlan } = usePlanGate()
const sectionTitlesEnabled = computed(() => authStore.canAccessBoolean('customSectionTitles'))
const sectionTitle = computed(() => props.sectionSettings.titles?.games || DEFAULT_SECTION_TITLES.games)
const updateSectionTitle = inject<(key: string, value: string) => void>('updateSectionTitle', () => {})

// Copy to clipboard handler
async function handleCopyHandle(game: GameItem) {
  if (!game.gamer_handle) return

  try {
    await navigator.clipboard.writeText(game.gamer_handle)

    toast.add({
      title: 'Copied!',
      icon: 'i-lucide-copy',
      color: 'success',
      duration: 2000
    })
  } catch {
    toast.add({
      title: 'Failed to copy',
      description: 'Please try again',
      icon: 'i-lucide-alert-circle',
      color: 'error',
      timeout: 3000
    })
  }
}

function getTooltipText(game: GameItem) {
  return game.gamer_handle ? `Copy: ${game.gamer_handle}` : game.game.name
}
</script>

<template>
  <div
    v-if="games.length > 0 || editMode"
    class="px-2"
    :class="{ 'mt-6': showTitles || !inCard }"
  >
    <!-- Section heading with divider -->
    <div
      v-if="showTitles || !inCard"
      class="flex items-center gap-3 mb-4"
    >
      <div
        class="flex-1 h-px"
        :style="dividerStyle"
      />
      <!-- Edit mode + Pro+ → click to edit -->
      <InlineEditText
        v-if="showTitles && editMode && sectionTitlesEnabled"
        :model-value="sectionSettings.titles?.games ?? ''"
        :editable="true"
        tag="h3"
        :placeholder="DEFAULT_SECTION_TITLES.games"
        :display-style="headingStyle"
        class="shrink-0"
        @update:model-value="updateSectionTitle('games', $event)"
      />
      <!-- Edit mode + Free → locked with Pro badge -->
      <div
        v-else-if="showTitles && editMode"
        class="shrink-0 inline-flex items-center gap-1.5 cursor-pointer rounded-lg transition-colors hover:bg-white/5 px-2 py-0.5"
        @click="requirePlan('Custom Section Titles', 'pro')"
      >
        <h3
          class="text-xs"
          :style="headingStyle"
        >
          {{ DEFAULT_SECTION_TITLES.games }}
        </h3>
        <UBadge
          color="fuchsia"
          variant="solid"
          size="xs"
        >
          <UIcon
            name="i-lucide-pencil"
            class="size-2.5"
          />
        </UBadge>
      </div>
      <!-- Public view → static text -->
      <h3
        v-else-if="showTitles"
        class="text-xs shrink-0"
        :style="headingStyle"
      >
        {{ sectionTitle }}
      </h3>
      <div
        v-if="showTitles"
        class="flex-1 h-px"
        :style="dividerStyle"
      />
    </div>

    <!-- Single game -->
    <div
      v-if="games.length === 1"
      class="flex justify-center"
    >
      <UTooltip :text="getTooltipText(games[0]!)">
        <button
          class="flex flex-col items-center gap-2 p-3 rounded-lg border border-white/6 bg-white/2 transition-all duration-200 hover:bg-white/5 hover:border-white/12 w-24 min-h-26"
          @click="handleCopyHandle(games[0]!)"
        >
          <div class="size-10 flex items-center justify-center">
            <img
              v-if="games[0]!.game.logo_url"
              :src="games[0]!.game.logo_url"
              :alt="games[0]!.game.name"
              class="w-full h-full object-contain"
            >
            <UIcon
              v-else
              name="i-lucide-gamepad-2"
              class="size-6 text-white/30"
            />
          </div>
          <p
            class="text-[10px] text-center leading-tight line-clamp-2"
            :style="{ color: `color-mix(in srgb, ${textSettings.color} 60%, transparent)` }"
          >
            {{ games[0]!.game.name }}
          </p>
          <span
            v-if="games[0]!.gamer_handle"
            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/6 border border-white/8 text-[9px] truncate max-w-full"
            :style="{ color: `color-mix(in srgb, ${textSettings.color} 50%, transparent)` }"
          >
            <UIcon
              name="i-lucide-copy"
              class="size-2.5 shrink-0 opacity-60"
            />
            {{ games[0]!.gamer_handle }}
          </span>
        </button>
      </UTooltip>
    </div>

    <!-- 2-4 games: row, no carousel -->
    <div
      v-else-if="games.length >= 2 && games.length <= 4"
      class="flex justify-center gap-2"
    >
      <UTooltip
        v-for="game in games"
        :key="game.id"
        :text="getTooltipText(game)"
      >
        <button
          class="flex flex-col items-center gap-2 p-3 rounded-lg border border-white/6 bg-white/2 transition-all duration-200 hover:bg-white/5 hover:border-white/12 w-24 min-h-26"
          @click="handleCopyHandle(game)"
        >
          <div class="size-10 flex items-center justify-center">
            <img
              v-if="game.game.logo_url"
              :src="game.game.logo_url"
              :alt="game.game.name"
              class="w-full h-full object-contain"
            >
            <UIcon
              v-else
              name="i-lucide-gamepad-2"
              class="size-6 text-white/30"
            />
          </div>
          <p
            class="text-[10px] text-center leading-tight line-clamp-2"
            :style="{ color: `color-mix(in srgb, ${textSettings.color} 60%, transparent)` }"
          >
            {{ game.game.name }}
          </p>
          <span
            v-if="game.gamer_handle"
            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/6 border border-white/8 text-[9px] truncate max-w-full"
            :style="{ color: `color-mix(in srgb, ${textSettings.color} 50%, transparent)` }"
          >
            <UIcon
              name="i-lucide-copy"
              class="size-2.5 shrink-0 opacity-60"
            />
            {{ game.gamer_handle }}
          </span>
        </button>
      </UTooltip>
    </div>

    <!-- 5+ games: carousel showing 4 at a time -->
    <div
      v-if="games.length > 4"
    >
      <UCarousel
        v-slot="{ item }"
        :items="carouselItems"
        arrows
        :prev="{ onClick: onClickPrev }"
        :next="{ onClick: onClickNext }"
        :ui="{
          container: '-ms-2',
          item: 'ps-2',
          prev: 'start-0 sm:start-0',
          next: 'end-0 sm:end-0'
        }"
        @select="onSelect"
      >
        <UTooltip :text="getTooltipText(asGame(item))">
          <button
            class="w-full flex flex-col items-center gap-2 p-3 rounded-lg border border-white/6 bg-white/2 transition-all duration-200 hover:bg-white/5 hover:border-white/12 h-full min-h-26"
            @click="handleCopyHandle(asGame(item))"
          >
            <div class="size-10 flex items-center justify-center">
              <img
                v-if="asGame(item).game.logo_url"
                :src="asGame(item).game.logo_url"
                :alt="asGame(item).game.name"
                class="w-full h-full object-contain"
              >
              <UIcon
                v-else
                name="i-lucide-gamepad-2"
                class="size-6 text-white/30"
              />
            </div>
            <p
              class="text-[10px] text-center leading-tight line-clamp-2"
              :style="{ color: `color-mix(in srgb, ${textSettings.color} 60%, transparent)` }"
            >
              {{ asGame(item).game.name }}
            </p>
            <span
              v-if="asGame(item).gamer_handle"
              class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/6 border border-white/8 text-[9px] truncate max-w-full"
              :style="{ color: `color-mix(in srgb, ${textSettings.color} 50%, transparent)` }"
            >
              <UIcon
                name="i-lucide-copy"
                class="size-2.5 shrink-0 opacity-60"
              />
              {{ asGame(item).gamer_handle }}
            </span>
          </button>
        </UTooltip>
      </UCarousel>
    </div>

    <!-- Empty state (edit mode only) -->
    <div
      v-if="games.length === 0 && editMode"
      class="flex flex-col items-center justify-center gap-3 py-8 rounded-lg border border-dashed border-white/10"
    >
      <UIcon
        name="i-lucide-gamepad-2"
        class="size-8 text-white/20"
      />
      <p class="text-sm text-white/30">
        No games yet
      </p>
    </div>

    <!-- Manage button (edit mode) -->
    <button
      v-if="editMode"
      class="mt-3 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-dashed transition-all duration-200 hover:scale-105 hover:border-white/15"
      :style="{
        borderColor: 'rgba(255, 255, 255, 0.08)',
        color: 'rgba(255, 255, 255, 0.4)'
      }"
      @click="emit('open-games-manager')"
    >
      <UIcon
        name="i-lucide-settings"
        class="size-4"
      />
      <span class="text-sm">Manage games</span>
    </button>
  </div>
</template>
